import { useTranslation } from 'react-i18next';
import { SectionSummary } from '@/shared/components/SectionSummary.tsx';
import { ClientInformationSummary } from '@/shared/components/ClientInformationSummary.tsx';
import { ClientInformationPosition } from '@/modules/position/components/ClientInformationPosition/ClientInformationPosition.tsx';
import { PositionSettings } from '@/modules/position/components/PositionSettings/PositionSettings.tsx';
import { PositionSummary } from '@/modules/position/components/PositionSummary/PositionSummary.tsx';
import { usePositionContext } from '@/shared/context/PositionContext.tsx';
import {
    Wizard,
    WizardClientSection,
    WizardSection,
    WizardSections,
    WizardSidebar,
    WizardSidebarSections,
} from '@/shared/components/Wizard.tsx';
import { PositionSettingsSectionSummary } from './PositionSettings/PositionSettingsSectionSummary.tsx';
import { PositionProductSelectionSummary } from './PositionSelectionSummary.tsx';
import { useSections } from '@/shared/hooks/useSections.ts';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import { usePlatformOrganizationId } from '@/clients/platformInfo/usePlatformInfo.ts';
import { useCallback } from 'react';
import { useBalanceSheetContext } from '@/shared/components/ProductGroupHeader/context/BalanceSheetContext.tsx';
import { BalanceSheet } from '@/shared/components/ProductGroupHeader/BalanceSheet.tsx';
import { FrancoIndicator } from '@/shared/components/ProductGroupHeader/components/FrancoIndicator.tsx';
import { ProcessingTypeSelection } from '@/modules/position/components/ProcessingTypeSelection.tsx';
import { MessageBox } from '@schuettflix/react-components';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';

const SectionSchema = z.union([
    z.literal('client-information'),
    z.literal('products'),
    z.literal('settings'),
    z.literal('terms'),
]);
type Section = z.infer<typeof SectionSchema>;

export function PositionWizard() {
    const { t } = useTranslation();
    const { isEditing, contextData } = usePositionContext();
    const { balanceProducts } = useBalanceSheetContext();
    const { marketCode } = useMarketContext();

    const platformOrganizationId = usePlatformOrganizationId();
    const customerInfo = contextData.getCustomerInformation();
    const businessCase = contextData.getBusinessCase();

    const [searchParams] = useSearchParams();
    const searchSectionResult = SectionSchema.safeParse(searchParams.get('section'));
    const searchSection = searchSectionResult.success ? searchSectionResult.data : 'products';
    const { goToNextSection, goToSection, isCurrentSection } = useSections<Section>({
        sections: ['client-information', 'products', 'settings', 'terms'],
        initialSection: isEditing ? searchSection : 'products',
    });

    const handleClientInformationEdit = useCallback(() => {
        goToSection('client-information');
    }, [goToSection]);

    const handleEditProductSelection = useCallback(() => {
        goToSection('products');
    }, [goToSection]);

    const handleEditSettings = useCallback(() => {
        goToSection('settings');
    }, [goToSection]);

    if (!platformOrganizationId) {
        return null;
    }

    return (
        <Wizard>
            <WizardSections>
                {isCurrentSection('client-information') && (
                    <WizardClientSection title={`1. ${t('sections.clientInformation.title')}`}>
                        <ClientInformationPosition onContinue={goToNextSection} />
                    </WizardClientSection>
                )}

                {isCurrentSection('products') && (
                    <WizardSection
                        title={`2. ${t('sections.productGroupSelection.title')}`}
                        headerAside={
                            balanceProducts.length ? (
                                <FrancoIndicator
                                    products={balanceProducts}
                                    productGroupType={businessCase?.pricingStrategy}
                                />
                            ) : undefined
                        }
                    >
                        <ProcessingTypeSelection onContinue={goToNextSection} />
                    </WizardSection>
                )}

                {isCurrentSection('settings') && (
                    <WizardSection title={`3. ${t('position.sections.positionSettings.title')}`}>
                        <PositionSettings onContinue={goToNextSection} />
                    </WizardSection>
                )}

                {isCurrentSection('terms') && (
                    <WizardSection title={`4. ${t('sections.summary.title')}`}>
                        <PositionSummary />
                    </WizardSection>
                )}
            </WizardSections>
            {!isCurrentSection('client-information') && (
                <WizardSidebar>
                    {balanceProducts.length && businessCase ? (
                        <BalanceSheet productGroupType={businessCase.pricingStrategy} products={balanceProducts} />
                    ) : null}
                    {marketCode === 'DE' && (
                        <MessageBox type="warning">
                            {t(`sections.productGroupSelection.legalCertificateHint`)}
                        </MessageBox>
                    )}
                    <WizardSidebarSections>
                        {customerInfo && (
                            <SectionSummary
                                title={`1. ${t('sections.clientInformation.title')}`}
                                onEditClick={handleClientInformationEdit}
                                editable
                            >
                                <ClientInformationSummary
                                    organizationId={customerInfo.organizationId}
                                    constructionProjectId={customerInfo.constructionProjectId}
                                    manualCostCenter={customerInfo.manualCostCenter}
                                />
                            </SectionSummary>
                        )}
                        {businessCase && (isCurrentSection('settings') || isCurrentSection('terms')) && (
                            <SectionSummary
                                title={`2. ${t('sections.productGroupSelection.title')}`}
                                onEditClick={handleEditProductSelection}
                                editable
                            >
                                <PositionProductSelectionSummary />
                            </SectionSummary>
                        )}
                        {isCurrentSection('terms') && (
                            <SectionSummary
                                title={`3. ${t('position.sections.positionSettings.title')}`}
                                onEditClick={handleEditSettings}
                                editable
                            >
                                <PositionSettingsSectionSummary />
                            </SectionSummary>
                        )}
                    </WizardSidebarSections>
                </WizardSidebar>
            )}
        </Wizard>
    );
}
